import { useState, useEffect } from 'react'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

interface PWAState {
  isInstallable: boolean
  isInstalled: boolean
  isStandalone: boolean
  deferredPrompt: BeforeInstallPromptEvent | null
  showPrompt: boolean
}

const INSTALL_PROMPT_DISMISSED_KEY = 'pwa-install-dismissed'
const DISMISS_EXPIRY_DAYS = 7

export const usePWA = () => {
  const [state, setState] = useState<PWAState>({
    isInstallable: false,
    isInstalled: false,
    isStandalone: false,
    deferredPrompt: null,
    showPrompt: false,
  })

  useEffect(() => {
    // Check if app is running in standalone mode (already installed)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes('android-app://')

    // Check if previously dismissed and still within expiry
    const dismissedData = localStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY)
    let shouldShowPrompt = true

    if (dismissedData) {
      try {
        const { timestamp } = JSON.parse(dismissedData)
        const daysSinceDismissal = (Date.now() - timestamp) / (1000 * 60 * 60 * 24)
        if (daysSinceDismissal < DISMISS_EXPIRY_DAYS) {
          shouldShowPrompt = false
        } else {
          // Expiry passed, remove the key
          localStorage.removeItem(INSTALL_PROMPT_DISMISSED_KEY)
        }
      } catch (error) {
        // Invalid data, remove it
        localStorage.removeItem(INSTALL_PROMPT_DISMISSED_KEY)
      }
    }

    setState((prev) => ({
      ...prev,
      isStandalone,
      isInstalled: isStandalone,
    }))

    // Handle beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      const promptEvent = e as BeforeInstallPromptEvent

      console.log('[PWA] Install prompt available')

      setState((prev) => ({
        ...prev,
        isInstallable: true,
        deferredPrompt: promptEvent,
        showPrompt: shouldShowPrompt && !isStandalone,
      }))
    }

    // Handle app installed event
    const handleAppInstalled = () => {
      console.log('[PWA] App installed successfully')

      setState((prev) => ({
        ...prev,
        isInstalled: true,
        isInstallable: false,
        deferredPrompt: null,
        showPrompt: false,
      }))

      // Clear dismissed status
      localStorage.removeItem(INSTALL_PROMPT_DISMISSED_KEY)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const installApp = async (): Promise<boolean> => {
    if (!state.deferredPrompt) {
      console.warn('[PWA] No install prompt available')
      return false
    }

    try {
      // Show the install prompt
      await state.deferredPrompt.prompt()

      // Wait for user choice
      const { outcome } = await state.deferredPrompt.userChoice

      console.log(`[PWA] User choice: ${outcome}`)

      if (outcome === 'accepted') {
        setState((prev) => ({
          ...prev,
          deferredPrompt: null,
          showPrompt: false,
        }))
        return true
      } else {
        dismissPrompt()
        return false
      }
    } catch (error) {
      console.error('[PWA] Install failed:', error)
      return false
    }
  }

  const dismissPrompt = () => {
    // Store dismissal with timestamp
    localStorage.setItem(
      INSTALL_PROMPT_DISMISSED_KEY,
      JSON.stringify({ timestamp: Date.now() })
    )

    setState((prev) => ({
      ...prev,
      showPrompt: false,
    }))
  }

  const showInstallPrompt = () => {
    if (state.isInstallable && !state.isInstalled) {
      localStorage.removeItem(INSTALL_PROMPT_DISMISSED_KEY)
      setState((prev) => ({
        ...prev,
        showPrompt: true,
      }))
    }
  }

  return {
    ...state,
    installApp,
    dismissPrompt,
    showInstallPrompt,
  }
}
